import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productService } from '../lib/firebaseService';
import { Plus, Calendar, MapPin, Users, ChevronRight, DollarSign, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import EventModal from '../components/EventModal';
import { t } from '../utils/translation';
import '../styles/components-extended.css';
import { format } from 'date-fns';

export default function Events() {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);

    const { data: events, isLoading } = useQuery({
        queryKey: ['events'],
        queryFn: productService.getEvents
    });

    const createMutation = useMutation({
        mutationFn: productService.addEvent,
        onSuccess: () => {
            queryClient.invalidateQueries(['events']);
            setIsModalOpen(false);
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => productService.updateEvent(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['events']);
            setIsModalOpen(false);
            setEditingEvent(null);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: productService.deleteEvent,
        onSuccess: () => {
            queryClient.invalidateQueries(['events']);
        }
    });

    const handleSave = async (data) => {
        if (editingEvent) {
            await updateMutation.mutateAsync({ id: editingEvent.id, data });
        } else {
            await createMutation.mutateAsync(data);
        }
    };

    const handleEdit = (e, event) => {
        e.preventDefault();
        e.stopPropagation();
        setEditingEvent(event);
        setIsModalOpen(true);
    };

    const handleDelete = async (e, id) => {
        e.preventDefault();
        e.stopPropagation();
        if (window.confirm(t('EVENTS.DELETE_CONFIRM'))) {
            await deleteMutation.mutateAsync(id);
        }
    };

    const handleCreate = () => {
        setEditingEvent(null);
        setIsModalOpen(true);
    };

    return (
        <div className="max-w-[1200px] mx-auto">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('EVENTS.TITLE')}</h1>
                    <p className="page-subtitle">{t('EVENTS.SUBTITLE')}</p>
                </div>
                <button onClick={handleCreate} className="btn btn-primary h-10 px-6 shadow-sm hover:shadow-md">
                    <Plus size={18} className="mr-2" />
                    {t('EVENTS.CREATE_BTN')}
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events?.map(event => {
                    const isPast = new Date(event.date) < new Date();
                    return (
                        <Link
                            to={`/events/${event.id}`}
                            key={event.id}
                            className="content-card !p-0 hover:!shadow-lg group relative border-l-4 border-l-transparent hover:border-l-purple-600 transition-all flex flex-col"
                        >
                            <div className="p-6 flex-1">
                                <div className="flex justify-between items-start mb-6">
                                    <div className={`badge ${isPast ? 'badge-gray' : 'badge-green'}`}>
                                        {isPast ? t('EVENTS.COMPLETED') : t('EVENTS.UPCOMING')}
                                    </div>
                                    <div className="flex gap-2 z-10">
                                        <button
                                            onClick={(e) => handleEdit(e, event)}
                                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                                            title={t('EVENTS.EDIT')}
                                        >
                                            {t('EVENTS.EDIT')}
                                        </button>
                                        <button
                                            onClick={(e) => handleDelete(e, event.id)}
                                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                            title={t('UI.DELETE_EVENT')}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-purple-600 transition-colors">
                                    {event.name}
                                </h3>

                                <div className="space-y-3 text-sm text-gray-500">
                                    <div className="flex items-center">
                                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 text-gray-400">
                                            <Calendar size={16} />
                                        </div>
                                        {format(new Date(event.date), 'EEE, MMM d, yyyy')}
                                    </div>
                                    <div className="flex items-center">
                                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 text-gray-400">
                                            <MapPin size={16} />
                                        </div>
                                        {event.venue || t('EVENTS.NO_VENUE')}
                                    </div>
                                    <div className="flex items-center">
                                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 text-gray-400">
                                            <Users size={16} />
                                        </div>
                                        {event.guestCount} {t('EVENTS.GUESTS')}
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex justify-between items-center rounded-b-[20px]">
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-400 uppercase font-semibold">{t('EVENTS.REVENUE')}</span>
                                    <span className="text-lg font-bold text-gray-900">€{event.revenue.toLocaleString()}</span>
                                </div>
                                <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 group-hover:bg-purple-600 group-hover:text-white group-hover:border-purple-600 transition-all">
                                    <ChevronRight size={16} />
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>

            <EventModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                event={editingEvent}
                onSave={handleSave}
            />
        </div>
    );
}
